import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

@Component({
  selector: 'app-add-po-amendment-form',
  templateUrl: './add-po-amendment-form.component.html',
  styleUrl: './add-po-amendment-form.component.css'
})
export class AddPoAmendmentFormComponent implements OnInit {
  poamendmentForm!: FormGroup;
  saving = false;
  purchaseOrders: any[] = [];
  lockedPoId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private readonly apiService: ApiService,
    private readonly toastrMsgService: ToastrMsgService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.poamendmentForm = this.fb.group({
      originalPoId: ['', Validators.required],
      amendmentType: ['Value change', Validators.required],
      reasonForAmendment: ['', Validators.required],
      revisedQuantity: [''],
      revisedUnitPrice: [''],
      revisedTotalValue: ['', Validators.required],
      authorizedBy: ['', Validators.required],
    });

    const queryPoId = Number(this.route.snapshot.queryParamMap.get('po_id'));
    if (queryPoId) {
      this.lockedPoId = queryPoId;
      this.poamendmentForm.patchValue({ originalPoId: queryPoId });
      this.poamendmentForm.get('originalPoId')?.disable();
      this.loadPo(queryPoId);
    }
    this.loadAmendablePos();
  }

  loadAmendablePos(): void {
    this.apiService.listPurchaseOrders().subscribe({
      next: (res: any) => {
        const rows = res?.data || [];
        this.purchaseOrders = rows.filter((po: any) =>
          ['issued', 'accepted', 'amended'].includes(String(po.po_status || '').toLowerCase())
        );
        if (this.lockedPoId && !this.purchaseOrders.some((po) => Number(po.id) === this.lockedPoId)) {
          this.apiService.getPurchaseOrderById(this.lockedPoId).subscribe({
            next: (poRes: any) => {
              const po = poRes?.data || poRes;
              if (po?.id) this.purchaseOrders = [po, ...this.purchaseOrders];
            },
          });
        }
      },
      error: (err) => this.toastrMsgService.error(err?.error?.message || 'Failed to load purchase orders'),
    });
  }

  onPoChange(poId: number): void {
    if (poId) this.loadPo(poId);
  }

  loadPo(poId: number): void {
    this.apiService.getPurchaseOrderById(poId).subscribe({
      next: (res: any) => {
        const po = res?.data || res;
        if (!po) return;
        this.poamendmentForm.patchValue({
          originalPoId: po.id,
          revisedQuantity: po.quantity || '',
          revisedUnitPrice: po.unit_price || '',
          revisedTotalValue: po.total_value || '',
        });
      },
    });
  }

  onSubmit(): void {
    if (this.poamendmentForm.invalid) {
      this.poamendmentForm.markAllAsTouched();
      return;
    }
    const v = this.poamendmentForm.getRawValue();
    const poId = Number(v.originalPoId);
    if (!poId) {
      this.toastrMsgService.error('Select a purchase order to amend');
      return;
    }
    this.saving = true;
    this.apiService.createPurchaseOrderAmendment(poId, {
      amendment_type: v.amendmentType,
      reason: v.reasonForAmendment,
      revised_quantity: v.revisedQuantity,
      revised_unit_price: v.revisedUnitPrice,
      revised_total_value: v.revisedTotalValue,
      authorized_by: v.authorizedBy,
    }).subscribe({
      next: () => {
        this.saving = false;
        this.toastrMsgService.success('Purchase order amended');
        this.router.navigate(['/home/purchase-order', poId]);
      },
      error: (err) => {
        this.saving = false;
        this.toastrMsgService.error(err?.error?.message || 'Amendment failed');
      },
    });
  }

  onReset(): void {
    const poId = this.lockedPoId || this.poamendmentForm.value.originalPoId;
    this.poamendmentForm.reset({
      originalPoId: poId || '',
      amendmentType: 'Value change',
      reasonForAmendment: '',
      revisedQuantity: '',
      revisedUnitPrice: '',
      revisedTotalValue: '',
      authorizedBy: '',
    });
    if (poId) this.loadPo(Number(poId));
  }
}
